import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';
import PaywallModal from '../components/PaywallModal';
import { canCreateProperty, getPropertyLimit, getUsageLabel } from '../lib/planLimits';

/* ── Design tokens ── */
const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  white:  '#fff',
  font:   "'Inter', system-ui, sans-serif",
};

/* ── Property types ── */
const PROP_TYPES = [
  { key:'wohnhaus',      label:'Gesamtes Wohn- und Geschäftshaus', desc:'Du besitzt das gesamte Wohn- und Geschäftshaus.',
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
  { key:'einheiten',     label:'Wohnungen / Gewerbeeinheiten',      desc:'Du besitzt eine oder mehrere einzelne Einheiten in einem Objekt.',
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="8" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="16" y2="21"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="2" y1="15" x2="22" y2="15"/></svg> },
  { key:'einfamilienhaus', label:'Einfamilienhaus',                 desc:'Du besitzt ein Einfamilienhaus.',
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
];

const PROP_TYPE_LABEL = { wohnhaus:'Wohnhaus', einheiten:'Einheiten', einfamilienhaus:'Einfamilienhaus', apartment:'Wohnung', house:'Haus', commercial:'Gewerbe' };

function fmtEur(v) { if (!v && v!==0) return null; return Number(v).toLocaleString('de-DE')+'  €'; }
function fullAddr(p) { return [p.street||p.address, p.house_number, [p.zip||p.postal_code, p.city].filter(Boolean).join(' ')].filter(Boolean).join(', '); }

function Sk({ w='100%', h=14, r=8 }) { return <div className="skeleton" style={{ width:w, height:h, borderRadius:r }} />; }

const labelStyle = { display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.5px', textTransform:'uppercase', color:T.muted, marginBottom:6 };
const inputSt = {
  width:'100%', border:`1.5px solid ${T.border}`, borderRadius:8,
  padding:'10px 12px', fontSize:13, boxSizing:'border-box',
  fontFamily:T.font, color:T.ink, background:'white', outline:'none',
};

/* ══════════════════════════════════════════════
   Berichte Modal
══════════════════════════════════════════════ */
function BerichteModal({ properties, tenants, onClose }) {
  const { addToast } = useToast();
  const [selected, setSelected] = useState('uebersicht');

  function download() {
    if (selected === 'uebersicht') {
      const header = ['Name','Typ','Straße','PLZ','Stadt','Einheiten','Kaltmiete','Fläche m²'];
      const rows = properties.map(p => [
        p.name, PROP_TYPE_LABEL[p.property_type||p.type]||'',
        p.street||p.address||'', p.zip||p.postal_code||'', p.city||'',
        p.units||0, p.cold_rent||'', p.area||'',
      ]);
      const csv = [header,...rows].map(r=>r.map(v=>`"${String(v??'').replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF'+csv], { type:'text/csv;charset=utf-8;' });
      const a = document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='objektuebersicht.csv'; a.click();
      addToast('Objektübersicht exportiert.','success');
    } else {
      const header = ['Objekt','Mieter','Einheit','Kaltmiete €/Mo','Mietbeginn'];
      const rows = tenants.map(t => {
        const prop = properties.find(p=>p.id===t.property_id);
        return [prop?.name||'', `${t.last_name||''}, ${t.first_name||''}`, t.unit||'', t.monthly_rent||'', t.lease_start||''];
      });
      const csv = [header,...rows].map(r=>r.map(v=>`"${String(v??'').replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF'+csv], { type:'text/csv;charset=utf-8;' });
      const a = document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='mietaufstellung.csv'; a.click();
      addToast('Mietaufstellung exportiert.','success');
    }
    onClose();
  }

  const REPORTS = [
    { key:'uebersicht', title:'Export der Objektübersicht', desc:'Dieser Bericht umfasst alle angezeigten Objekte (inklusive Filter) und enthält neben den Stammdaten eine Übersicht über die Einheiten und Mietverhältnisse.' },
    { key:'mietaufstellung', title:'Mietaufstellung (einfach)', desc:'Dieser Bericht umfasst die Mietaufstellung aller aktuellen Mieter (exklusive Filter). Häufige Nutzung: Monatlicher Report an die Bank.' },
  ];

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{ position:'fixed',inset:0,zIndex:100,background:'rgba(17,24,39,0.5)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div style={{ background:'white',borderRadius:16,width:'100%',maxWidth:600,boxShadow:'0 24px 64px rgba(0,0,0,0.16)',overflow:'hidden' }}>
        <div style={{ padding:'22px 28px 18px',borderBottom:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
          <h2 style={{ margin:0,fontSize:17,fontWeight:800,color:T.ink }}>Objektberichte</h2>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',color:T.muted,padding:4,display:'flex',borderRadius:6 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding:'20px 28px' }}>
          <p style={{ fontSize:13,color:T.muted,margin:'0 0 16px' }}>Wähle den gewünschten Bericht aus:</p>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            {REPORTS.map(r=>(
              <div key={r.key} onClick={()=>setSelected(r.key)} style={{ border:`2px solid ${selected===r.key?T.ink:T.border}`,borderRadius:12,padding:'18px 16px',cursor:'pointer',transition:'border-color 0.15s',background:selected===r.key?'#f5f5f5':'white' }}>
                <div style={{ fontSize:14,fontWeight:700,color:T.ink,marginBottom:10,lineHeight:1.4 }}>{r.title}</div>
                <div style={{ fontSize:12,color:T.muted,lineHeight:1.55,marginBottom:14 }}>{r.desc}</div>
                <button onClick={e=>{e.stopPropagation();setSelected(r.key);}} style={{ width:'100%',padding:'8px',borderRadius:8,border:'none',background:selected===r.key?T.ink:'#d1d5db',color:'white',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>
                  Auswählen
                </button>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding:'14px 28px 22px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 18px',borderRadius:8,border:`1.5px solid ${T.border}`,background:'white',color:T.ink,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>Abbrechen</button>
          <button onClick={download} style={{ padding:'9px 20px',borderRadius:8,border:'none',background:T.ink,color:'white',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>Bericht erstellen</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Wizard Step 1 — Objekttyp wählen
══════════════════════════════════════════════ */
function WizardStep1({ onBack, onNext, isMobile }) {
  const [selected, setSelected] = useState(null);
  return (
    <div style={{ minHeight:'100vh',background:T.bg,fontFamily:T.font }}>
      {/* Top bar */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:isMobile?'14px 16px':'20px 36px',borderBottom:`1px solid ${T.border}`,background:'white',gap:8,flexWrap:'wrap' }}>
        <button onClick={onBack} style={{ background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:13,color:T.ink,fontFamily:T.font,fontWeight:600 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          Zurück
        </button>
        <span style={{ fontSize:12,color:T.muted,fontWeight:600 }}>1/2 Schritte</span>
      </div>

      {/* Content */}
      <div style={{ maxWidth:900,margin:isMobile?'24px auto':'60px auto',padding:'0 16px' }}>
        <h1 style={{ fontSize:isMobile?22:32,fontWeight:900,letterSpacing:'-1px',color:T.ink,marginBottom:isMobile?24:48,lineHeight:1.2 }}>
          Wähle den Objekt-Typ. Um was für eine Immobilie handelt es sich?
        </h1>
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)',gap:isMobile?12:20 }}>
          {PROP_TYPES.map(pt=>(
            <div key={pt.key} onClick={()=>setSelected(pt.key)} style={{
              background:'white',border:`2px solid ${selected===pt.key?T.ink:T.border}`,
              borderRadius:14,padding:'32px 24px',cursor:'pointer',textAlign:'center',
              transition:'border-color 0.15s,box-shadow 0.15s',
              boxShadow:selected===pt.key?`0 0 0 3px ${T.border}`:'none',
            }}>
              <div style={{ display:'flex',justifyContent:'center',marginBottom:16,opacity:0.7 }}>{pt.icon}</div>
              <div style={{ fontSize:15,fontWeight:700,color:T.ink,marginBottom:10,lineHeight:1.3 }}>{pt.label}</div>
              <div style={{ fontSize:12,color:T.muted,lineHeight:1.6,marginBottom:24 }}>{pt.desc}</div>
              <button
                onClick={e=>{e.stopPropagation();setSelected(pt.key);}}
                style={{ width:'100%',padding:'9px',borderRadius:8,border:'none',background:selected===pt.key?T.ink:'#e5e5e5',color:selected===pt.key?'white':T.ink,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:T.font,transition:'background 0.15s,color 0.15s' }}
              >
                Auswählen
              </button>
            </div>
          ))}
        </div>
        {selected && (
          <div style={{ textAlign:'right',marginTop:32 }}>
            <button onClick={()=>onNext(selected)} style={{ padding:'12px 32px',borderRadius:10,border:'none',background:T.ink,color:'white',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>
              Weiter →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Wizard Step 2 — Eckdaten
══════════════════════════════════════════════ */
function WizardStep2({ propertyType, onBack, onSave, isMobile }) {
  const [form, setForm] = useState({ name:'', street:'', house_number:'', zip:'', city:'', country:'Deutschland' });
  const [saving, setSaving]       = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg]   = useState(false);
  const debounceRef = useRef(null);
  const ready = form.name.trim() && form.street.trim();

  function set(k,v) { setForm(f=>({...f,[k]:v})); }

  /* Nominatim address autocomplete */
  function handleStreetChange(v) {
    set('street', v);
    clearTimeout(debounceRef.current);
    if (v.length < 3) { setSuggestions([]); setShowSugg(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=de,at,ch&q=${encodeURIComponent(v)}`,
          { headers: { 'Accept-Language': 'de' } }
        );
        const data = await res.json();
        setSuggestions(data);
        setShowSugg(data.length > 0);
      } catch { /* ignore */ }
    }, 400);
  }

  function applySuggestion(item) {
    const a = item.address || {};
    const road = a.road || a.pedestrian || a.path || '';
    const houseNum = a.house_number || '';
    const postcode = a.postcode || '';
    const city = a.city || a.town || a.village || a.municipality || '';
    const country = a.country || 'Deutschland';
    setForm(f => ({ ...f, street: road, house_number: houseNum, zip: postcode, city, country }));
    setSuggestions([]);
    setShowSugg(false);
  }

  async function handleSave() {
    if (!ready) return;
    setSaving(true);
    await onSave({ name:form.name, street:form.street, house_number:form.house_number, zip:form.zip, city:form.city, country:form.country, address:form.street, property_type:propertyType });
    setSaving(false);
  }

  const typLabel = PROP_TYPES.find(t=>t.key===propertyType)?.label || 'Haus';

  return (
    <div style={{ minHeight:'100vh',background:T.bg,fontFamily:T.font }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:isMobile?'14px 16px':'20px 36px',borderBottom:`1px solid ${T.border}`,background:'white',gap:8,flexWrap:'wrap' }}>
        <button onClick={onBack} style={{ background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:13,color:T.ink,fontFamily:T.font,fontWeight:600 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          Zurück
        </button>
        {!isMobile && <span style={{ fontSize:13,color:T.muted,fontWeight:600 }}>2/2 Schritte</span>}
        <button
          onClick={handleSave} disabled={!ready||saving}
          style={{ padding:isMobile?'8px 16px':'10px 24px',borderRadius:9,border:'none',background:ready?T.ink:'#d1d5db',color:'white',fontSize:13,fontWeight:700,cursor:ready?'pointer':'not-allowed',fontFamily:T.font }}
        >
          {saving?'Wird angelegt…':'Objekt anlegen'}
        </button>
      </div>

      <div style={{ maxWidth:640,margin:isMobile?'24px auto':'60px auto',padding:'0 16px' }}>
        <h1 style={{ fontSize:isMobile?20:30,fontWeight:900,letterSpacing:'-0.8px',color:T.ink,marginBottom:isMobile?20:36,lineHeight:1.25 }}>
          Erzähle uns mehr über das Objekt.
        </h1>

        <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:14,padding:'28px 28px 24px' }}>
          <h3 style={{ margin:'0 0 8px',fontSize:18,fontWeight:800,color:T.ink }}>Eckdaten</h3>
          <p style={{ margin:'0 0 16px',fontSize:13,color:T.muted }}>Lege einen Namen für das {typLabel} an</p>
          <input
            value={form.name} onChange={e=>set('name',e.target.value)}
            placeholder={typLabel + '-Name'}
            style={{ ...inputSt, marginBottom:24 }}
            onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}
          />

          <h3 style={{ margin:'0 0 16px',fontSize:18,fontWeight:800,color:T.ink }}>Adresse</h3>
          <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'2fr 1fr',gap:12,marginBottom:12 }}>
            <div style={{ position:'relative' }}>
              <label style={labelStyle}>Strasse</label>
              <input
                value={form.street}
                onChange={e=>handleStreetChange(e.target.value)}
                onBlur={()=>setTimeout(()=>setShowSugg(false),200)}
                onFocus={e=>{e.target.style.borderColor=T.ink; if(suggestions.length)setShowSugg(true);}}
                placeholder="Straße eingeben…"
                style={inputSt}
                autoComplete="off"
              />
              {showSugg && suggestions.length > 0 && (
                <div style={{ position:'absolute',top:'100%',left:0,right:0,zIndex:50,background:'white',border:`1px solid ${T.border}`,borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',marginTop:4,overflow:'hidden' }}>
                  {suggestions.map((s,i)=>(
                    <div key={i} onMouseDown={()=>applySuggestion(s)}
                      style={{ padding:'10px 14px',fontSize:12,color:T.ink,cursor:'pointer',borderBottom:i<suggestions.length-1?`1px solid ${T.border}`:'none',lineHeight:1.4 }}
                      onMouseEnter={e=>e.currentTarget.style.background='#f5f5f5'}
                      onMouseLeave={e=>e.currentTarget.style.background='white'}
                    >
                      <div style={{ fontWeight:600 }}>{(s.address?.road||s.address?.pedestrian||'')} {s.address?.house_number||''}</div>
                      <div style={{ color:T.muted,fontSize:11,marginTop:2 }}>{[s.address?.postcode, s.address?.city||s.address?.town||s.address?.village, s.address?.country].filter(Boolean).join(', ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={labelStyle}>Hausnummer</label>
              <input value={form.house_number} onChange={e=>set('house_number',e.target.value)} style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
            </div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 2fr',gap:12,marginBottom:12 }}>
            <div>
              <label style={labelStyle}>PLZ</label>
              <input value={form.zip} onChange={e=>set('zip',e.target.value)} style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
            </div>
            <div>
              <label style={labelStyle}>Stadt</label>
              <input value={form.city} onChange={e=>set('city',e.target.value)} style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Land</label>
            <div style={{ position:'relative' }}>
              <input value={form.country} onChange={e=>set('country',e.target.value)} style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
              {form.country && (
                <button onClick={()=>set('country','')} style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:T.muted,display:'flex',padding:2 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Property Detail View
══════════════════════════════════════════════ */
function PropertyDetail({ property: initProp, tenants, onBack, onDeleted, isNew }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);

  useEffect(() => {
    supabase.from('units').select('id').eq('property_id', initProp.id).eq('owner_id', user.id)
      .then(({ data }) => setUnits(data || []));
  }, [initProp.id]);
  const [prop, setProp]     = useState(initProp);
  const [activeTab, setTab] = useState('uebersicht');
  const [showBerichte, setShowBerichte] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name:prop.name||'', street:prop.street||prop.address||'', house_number:prop.house_number||'', postal_code:prop.zip||prop.postal_code||'', city:prop.city||'', country:prop.country||'Deutschland', area:prop.area||'', cold_rent:prop.cold_rent||'', notes:prop.notes||'' });

  const assigned = tenants.filter(t=>t.property_id===prop.id);
  const addr = fullAddr(prop);
  const mapsQuery = encodeURIComponent(addr || prop.name);

  async function saveEdit() {
    const payload = { ...editForm, address:editForm.street, zip:editForm.postal_code, area:editForm.area||null, cold_rent:editForm.cold_rent||null };
    const { error } = await supabase.from('properties').update(payload).eq('id',prop.id).eq('owner_id',user.id);
    if (error) { addToast('Fehler beim Speichern.','error'); return; }
    setProp(p=>({...p,...payload}));
    setEditing(false);
    addToast('Objekt aktualisiert.','success');
  }

  async function handleDelete() {
    if (!window.confirm('Objekt wirklich löschen? Alle zugehörigen Daten werden entfernt.')) return;
    await supabase.from('properties').delete().eq('id',prop.id).eq('owner_id',user.id);
    addToast('Objekt gelöscht.','success');
    onDeleted(prop.id);
  }

  return (
    <div style={{ display:'flex',gap:0,minHeight:'calc(100vh - 56px)',fontFamily:T.font,color:T.ink,margin:isMobile?'0 -16px':'0 -36px' }}>

      {/* ── Left sidebar ── */}
      {!isMobile && (
        <div style={{ width:280,borderRight:`1px solid ${T.border}`,flexShrink:0,padding:'24px 20px',background:'white',overflowY:'auto' }}>
          <button onClick={onBack} style={{ background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:13,color:T.muted,fontFamily:T.font,marginBottom:20,padding:0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            Zurück zur Übersicht
          </button>

          {/* Property card */}
          <div style={{ border:`2px solid ${T.ink}`,borderRadius:12,padding:'16px',marginBottom:16 }}>
            <div style={{ fontSize:14,fontWeight:800,color:T.ink,marginBottom:4 }}>{prop.name}</div>
            {addr && <div style={{ fontSize:12,color:T.muted,lineHeight:1.5 }}>{addr}</div>}
            <div style={{ display:'flex',gap:0,marginTop:16,borderTop:`1px solid ${T.border}`,paddingTop:14,justifyContent:'space-around' }}>
              {[
                { label:'Nebenkosten', icon:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg> },
                { label:'Mieterportal', icon:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> },
                { label:'Mietverträge', icon:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
              ].map(item=>(
                <div key={item.label} style={{ textAlign:'center',cursor:'pointer' }} onClick={()=>addToast(`${item.label} — demnächst verfügbar`,'info')}>
                  <div style={{ width:38,height:38,borderRadius:9,background:T.bg,border:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 5px',color:T.ink }}>{item.icon}</div>
                  <div style={{ fontSize:10,color:T.muted,lineHeight:1.3 }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Units — next step CTA */}
          {units.length === 0 ? (
            <div style={{ background:T.ink,borderRadius:12,padding:'18px',color:'white' }}>
              <div style={{ fontSize:10,fontWeight:700,letterSpacing:'1px',textTransform:'uppercase',opacity:0.5,marginBottom:6 }}>Schritt 2 von 4</div>
              <div style={{ fontSize:13,fontWeight:700,marginBottom:6,lineHeight:1.4 }}>Erste Einheit erstellen</div>
              <div style={{ fontSize:12,opacity:0.6,marginBottom:14,lineHeight:1.5 }}>Dein Objekt ist erstellt. Füge jetzt eine Wohnung oder Gewerbeeinheit hinzu, damit du Mieter und Zahlungen verwalten kannst.</div>
              <button
                onClick={() => navigate(`/units?property=${initProp.id}`)}
                style={{ background:'white',border:'none',color:T.ink,borderRadius:8,padding:'9px 12px',fontSize:12,fontWeight:800,cursor:'pointer',fontFamily:T.font,width:'100%' }}
              >
                Erste Einheit erstellen →
              </button>
            </div>
          ) : (
            <div style={{ background:'#f0fdf4',borderRadius:12,padding:'14px 16px',border:'1px solid #bbf7d0' }}>
              <div style={{ fontSize:12,fontWeight:700,color:'#15803d',marginBottom:2 }}>✓ {units.length} Einheit{units.length !== 1 ? 'en' : ''}</div>
              <button onClick={() => navigate(`/units?property=${initProp.id}`)} style={{ background:'none',border:'none',color:'#15803d',fontSize:11,fontWeight:600,cursor:'pointer',padding:0,fontFamily:T.font }}>
                Einheiten verwalten →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Right content ── */}
      <div style={{ flex:1,overflowY:'auto',padding:isMobile?'20px 16px':'28px 32px' }}>

        {isMobile && (
          <button onClick={onBack} style={{ background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:13,color:T.muted,fontFamily:T.font,marginBottom:16,padding:0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            Zurück zur Übersicht
          </button>
        )}

        {/* Header */}
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,flexWrap:'wrap',gap:10 }}>
          <h2 style={{ margin:0,fontSize:22,fontWeight:800,letterSpacing:'-0.4px' }}>{prop.name}</h2>
          <div style={{ display:'flex',gap:8 }}>
            <button onClick={()=>setShowBerichte(true)} style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:8,border:`1.5px solid ${T.border}`,background:'white',color:T.ink,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Berichte
            </button>
            <button onClick={()=>setEditing(true)} style={{ padding:'8px 14px',borderRadius:8,border:`1.5px solid ${T.border}`,background:'white',color:T.ink,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>Ändern</button>
            <button onClick={handleDelete} style={{ padding:'8px 14px',borderRadius:8,border:'1.5px solid #fca5a5',background:'white',color:'#dc2626',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>Löschen</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex',borderBottom:`1px solid ${T.border}`,marginBottom:24 }}>
          {[{key:'uebersicht',label:'Übersicht'},{key:'abrechnung',label:'Abrechnungsdaten'}].map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)} style={{ padding:'9px 16px',border:'none',background:'none',cursor:'pointer',fontSize:13,fontWeight:activeTab===t.key?700:400,color:activeTab===t.key?T.ink:T.muted,fontFamily:T.font,borderBottom:`2px solid ${activeTab===t.key?T.ink:'transparent'}`,marginBottom:-1 }}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab==='uebersicht' && (
          <>
            {/* ── Nächster Schritt Banner (wenn keine Einheiten) ── */}
            {units.length === 0 && (
              <div style={{ background:'linear-gradient(135deg,#111 0%,#1e293b 100%)',borderRadius:14,padding:'22px 24px',marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:14 }}>
                <div>
                  <div style={{ fontSize:10,fontWeight:700,letterSpacing:'1.5px',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:6 }}>
                    Schritt 1 ✓ &nbsp;·&nbsp; <span style={{ color:'#60a5fa' }}>Schritt 2 von 4</span>
                  </div>
                  <div style={{ fontSize:16,fontWeight:800,color:'white',marginBottom:4,letterSpacing:'-0.3px' }}>
                    Jetzt Einheit erstellen
                  </div>
                  <div style={{ fontSize:12,color:'rgba(255,255,255,0.55)',lineHeight:1.6,maxWidth:400 }}>
                    Dein Objekt ist erstellt. Füge eine Wohnung oder Gewerbeeinheit hinzu, damit du Mieter und Zahlungen verwalten kannst.
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/units?property=${initProp.id}`)}
                  style={{ background:'white',border:'none',color:T.ink,borderRadius:10,padding:'11px 22px',fontSize:13,fontWeight:800,cursor:'pointer',fontFamily:T.font,whiteSpace:'nowrap',flexShrink:0 }}
                >
                  Erste Einheit erstellen →
                </button>
              </div>
            )}

            {/* Eckdaten + Map */}
            <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:16,marginBottom:16 }}>
              <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:12,padding:'20px' }}>
                <h4 style={{ margin:'0 0 14px',fontSize:13,fontWeight:700,color:T.ink }}>Eckdaten</h4>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
                  {[
                    { label:`${fmtEur(prop.cold_rent)||'0 €'}`, sub:'Kaltmiete' },
                    { label:'–', sub:'∅ Miete / m² (Wohnung)' },
                    { label:'–', sub:'∅ Miete / m² (Gewerbe)' },
                    { label:`${prop.area||0} m²`, sub:'Fläche' },
                  ].map((item,i)=>(
                    <div key={i}>
                      <div style={{ fontSize:15,fontWeight:700,color:T.ink,marginBottom:2 }}>{item.label}</div>
                      <div style={{ fontSize:11,color:T.muted }}>{item.sub}</div>
                    </div>
                  ))}
                </div>
                {addr && (
                  <div style={{ marginTop:14,paddingTop:12,borderTop:`1px solid ${T.border}`,display:'flex',gap:6,alignItems:'center' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    <span style={{ fontSize:11,color:T.muted }}>{addr}</span>
                  </div>
                )}
              </div>

              <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:12,overflow:'hidden',minHeight:180 }}>
                <div style={{ padding:'10px 14px',borderBottom:`1px solid ${T.border}` }}>
                  <span style={{ fontSize:12,fontWeight:600,color:T.ink }}>In Maps öffnen ↗</span>
                </div>
                <iframe title="map" src={`https://maps.google.com/maps?q=${mapsQuery}&output=embed&z=15`} style={{ width:'100%',height:160,border:'none' }} loading="lazy" />
              </div>
            </div>

            {/* Mietverhältnisse */}
            <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:12,overflow:'hidden',marginBottom:16 }}>
              <div style={{ padding:'14px 20px',borderBottom:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                <h4 style={{ margin:0,fontSize:13,fontWeight:700,color:T.ink }}>Mietverhältnisse</h4>
                <span style={{ fontSize:12,color:T.muted }}>{assigned.length} Mieter</span>
              </div>
              {assigned.length === 0 ? (
                <div style={{ padding:'28px 20px',textAlign:'center' }}>
                  <div style={{ fontSize:13,fontWeight:600,color:T.ink,marginBottom:6 }}>Noch keine Mieter zugeordnet</div>
                  <div style={{ fontSize:12,color:T.muted }}>Gehe zu <strong>Mieter</strong>, um ein Mietverhältnis für dieses Objekt anzulegen.</div>
                </div>
              ) : (
                assigned.map((t,i)=>(
                  <div key={t.id} style={{ display:'grid',gridTemplateColumns:'1fr auto auto',alignItems:'center',padding:'12px 20px',borderBottom:i<assigned.length-1?`1px solid ${T.border}`:'none',gap:16 }}>
                    <div>
                      <div style={{ fontSize:13,fontWeight:600,color:T.ink }}>{t.last_name}, {t.first_name}</div>
                      {t.unit && <div style={{ fontSize:11,color:T.muted,marginTop:1 }}>Einheit {t.unit}</div>}
                    </div>
                    <div style={{ fontSize:12,color:T.muted }}>{t.monthly_rent?`${fmtEur(t.monthly_rent)} / Mo.`:'–'}</div>
                    <div style={{ fontSize:11,color:T.muted }}>{t.lease_start?new Date(t.lease_start).toLocaleDateString('de-DE',{day:'2-digit',month:'2-digit',year:'numeric'}):''}</div>
                  </div>
                ))
              )}
            </div>

            {/* Dokumente + Notizen */}
            <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:16 }}>
              <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:12,padding:'20px' }}>
                <h4 style={{ margin:'0 0 12px',fontSize:13,fontWeight:700,color:T.ink }}>Dokumente</h4>
                <div style={{ textAlign:'center',padding:'16px 0' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom:10 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                  <div style={{ fontSize:12,fontWeight:600,color:T.ink,marginBottom:4 }}>Keine Dokumente</div>
                  <div style={{ fontSize:11,color:T.muted,marginBottom:12 }}>Noch keine Dokumente zu diesem Objekt.</div>
                  <button onClick={()=>addToast('Dokumente hochladen — demnächst verfügbar','info')} style={{ background:T.ink,color:'white',border:'none',borderRadius:8,padding:'8px 14px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>Hochladen</button>
                </div>
              </div>
              <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:12,padding:'20px' }}>
                <h4 style={{ margin:'0 0 12px',fontSize:13,fontWeight:700,color:T.ink }}>Notizen</h4>
                {prop.notes ? (
                  <p style={{ margin:0,fontSize:13,color:T.muted,lineHeight:1.7,whiteSpace:'pre-wrap' }}>{prop.notes}</p>
                ) : (
                  <div style={{ textAlign:'center',padding:'16px 0' }}>
                    <div style={{ fontSize:12,fontWeight:600,color:T.ink,marginBottom:4 }}>Keine Notizen</div>
                    <button onClick={()=>setEditing(true)} style={{ background:'none',border:'none',color:T.ink,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>+ Notiz erstellen</button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab==='abrechnung' && (
          <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:12,padding:'40px',textAlign:'center' }}>
            <div style={{ fontSize:13,color:T.muted }}>Abrechnungsdaten werden demnächst verfügbar sein.</div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div onClick={e=>{if(e.target===e.currentTarget)setEditing(false);}} style={{ position:'fixed',inset:0,zIndex:100,background:'rgba(17,24,39,0.5)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
          <div style={{ background:'white',borderRadius:16,width:'100%',maxWidth:500,boxShadow:'0 24px 64px rgba(0,0,0,0.16)',overflow:'hidden' }}>
            <div style={{ padding:'22px 28px 18px',borderBottom:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <h2 style={{ margin:0,fontSize:17,fontWeight:800,color:T.ink }}>Objekt bearbeiten</h2>
              <button onClick={()=>setEditing(false)} style={{ background:'none',border:'none',cursor:'pointer',color:T.muted,padding:4,display:'flex' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding:'20px 28px 24px',maxHeight:'70vh',overflowY:'auto' }}>
              {[{k:'name',l:'Name *'},{k:'street',l:'Straße'},{k:'house_number',l:'Hausnummer'},{k:'postal_code',l:'PLZ'},{k:'city',l:'Stadt'},{k:'country',l:'Land'},{k:'area',l:'Fläche m²'},{k:'cold_rent',l:'Kaltmiete €/Mo'}].map(({k,l})=>(
                <div key={k} style={{ marginBottom:14 }}>
                  <label style={labelStyle}>{l}</label>
                  <input value={editForm[k]||''} onChange={e=>setEditForm(f=>({...f,[k]:e.target.value}))} style={inputSt}
                    onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
                </div>
              ))}
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>Notizen</label>
                <textarea value={editForm.notes||''} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} rows={3} style={{ ...inputSt,resize:'vertical',lineHeight:1.5 }}
                  onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
              </div>
            </div>
            <div style={{ padding:'14px 28px 20px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
              <button onClick={()=>setEditing(false)} style={{ padding:'9px 18px',borderRadius:8,border:`1.5px solid ${T.border}`,background:'white',color:T.ink,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>Abbrechen</button>
              <button onClick={saveEdit} style={{ padding:'9px 20px',borderRadius:8,border:'none',background:T.ink,color:'white',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {showBerichte && (
        <BerichteModal properties={[prop]} tenants={assigned} onClose={()=>setShowBerichte(false)} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Properties Page
══════════════════════════════════════════════ */
export default function Properties() {
  const { user, subscription } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const limit = getPropertyLimit(subscription);

  const [properties, setProperties] = useState([]);
  const [tenants, setTenants]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showBerichte, setShowBerichte] = useState(false);

  const navigate = useNavigate();

  /* view: 'list' | 'wizard-type' | 'wizard-form' | 'detail' */
  const [view, setView]               = useState('list');
  const [wizardType, setWizardType]   = useState(null);
  const [detailProp, setDetailProp]   = useState(null);
  const [newPropId, setNewPropId]     = useState(null);
  const [showFirstUnitModal, setShowFirstUnitModal] = useState(false);

  /* filters */
  const [search, setSearch]           = useState('');
  const [filterKategorie, setFilterK] = useState(null);
  const [filterZahlung, setFilterZ]   = useState(null);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const [{ data:p }, { data:t }] = await Promise.all([
      supabase.from('properties').select('*').eq('owner_id',user.id).order('name'),
      supabase.from('tenants').select('id,first_name,last_name,unit,lease_start,lease_end,monthly_rent,property_id').eq('owner_id',user.id).order('last_name'),
    ]);
    setProperties(p||[]);
    setTenants(t||[]);
    setLoading(false);
  }

  async function handleWizardSave(formData) {
    if (!canCreateProperty(subscription, properties.length)) { setShowPaywall(true); return; }
    const payload = { name:formData.name, street:formData.street, house_number:formData.house_number, zip:formData.zip, city:formData.city, country:formData.country, address:formData.street, property_type:formData.property_type, type:formData.property_type, units:1, owner_id:user.id };
    const { error, data } = await supabase.from('properties').insert(payload).select().single();
    if (error) { addToast('Fehler beim Anlegen.','error'); console.error(error); return; }
    await fetchAll();
    addToast('Objekt erfolgreich erstellt.','success');
    setDetailProp(data);
    setNewPropId(data.id);
    setShowFirstUnitModal(true);
    setView('detail');
  }

  function startCreate() {
    if (!canCreateProperty(subscription, properties.length)) { setShowPaywall(true); return; }
    setView('wizard-type');
  }

  const filtered = properties.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name?.toLowerCase().includes(q) || fullAddr(p).toLowerCase().includes(q);
    const matchK = !filterKategorie || (p.property_type||p.type) === filterKategorie;
    return matchSearch && matchK;
  });

  /* ── Wizard views (full-screen, outside Layout padding) ── */
  if (view==='wizard-type') return <WizardStep1 onBack={()=>setView('list')} onNext={type=>{setWizardType(type);setView('wizard-form');}} isMobile={isMobile} />;
  if (view==='wizard-form') return <WizardStep2 propertyType={wizardType} onBack={()=>setView('wizard-type')} onSave={handleWizardSave} isMobile={isMobile} />;

  /* ── Detail view ── */
  if (view==='detail' && detailProp) return (
    <>
      <PropertyDetail
        property={detailProp}
        tenants={tenants}
        onBack={()=>{fetchAll();setView('list');}}
        onDeleted={id=>{setProperties(prev=>prev.filter(p=>p.id!==id));setView('list');}}
        isNew={showFirstUnitModal}
      />

      {/* First Unit Modal — shown after property creation */}
      {showFirstUnitModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200,padding:24 }}>
          <div style={{ background:'white',borderRadius:20,width:'100%',maxWidth:460,padding:'36px 36px 32px',boxShadow:'0 24px 80px rgba(0,0,0,0.2)',textAlign:'center' }}>

            {/* Success icon */}
            <div style={{ width:56,height:56,background:'#f0fdf4',border:'2px solid #bbf7d0',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>

            {/* Steps */}
            <div style={{ display:'flex',justifyContent:'center',gap:6,marginBottom:20 }}>
              {['Objekt','Einheit','Mieter','Zahlung'].map((s,i)=>(
                <div key={s} style={{ display:'flex',alignItems:'center',gap:6 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:4 }}>
                    <div style={{ width:22,height:22,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:800,
                      background: i===0 ? '#16a34a' : i===1 ? T.ink : '#e5e7eb',
                      color: i<=1 ? 'white' : '#9ca3af',
                    }}>
                      {i===0 ? '✓' : i+1}
                    </div>
                    <span style={{ fontSize:11,fontWeight:i===1?700:400,color:i===1?T.ink:'#9ca3af' }}>{s}</span>
                  </div>
                  {i < 3 && <div style={{ width:16,height:1,background:'#e5e7eb' }}/>}
                </div>
              ))}
            </div>

            <h2 style={{ margin:'0 0 10px',fontSize:20,fontWeight:800,letterSpacing:'-0.4px',color:T.ink }}>
              Erste Einheit erstellen
            </h2>
            <p style={{ margin:'0 0 28px',fontSize:13,color:'#6b7280',lineHeight:1.7 }}>
              Dein Objekt <strong style={{ color:T.ink }}>{detailProp.name}</strong> ist erstellt.<br/>
              Füge jetzt die erste Wohnung oder Gewerbeeinheit hinzu, damit du Mieter und Zahlungen verwalten kannst.
            </p>

            <button
              onClick={() => { setShowFirstUnitModal(false); navigate(`/units?property=${detailProp.id}`); }}
              style={{ width:'100%',height:46,background:T.ink,border:'none',borderRadius:11,color:'white',fontSize:14,fontWeight:800,cursor:'pointer',fontFamily:T.font,marginBottom:10,letterSpacing:'-0.2px' }}
            >
              Einheit jetzt erstellen →
            </button>
            <button
              onClick={() => setShowFirstUnitModal(false)}
              style={{ width:'100%',height:40,background:'none',border:`1.5px solid ${T.border}`,borderRadius:11,color:T.muted,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:T.font }}
            >
              Später
            </button>
          </div>
        </div>
      )}
    </>
  );

  /* ── List view ── */
  return (
    <div style={{ maxWidth:1100,fontFamily:T.font,color:T.ink }}>
      {showPaywall && (
        <PaywallModal onClose={()=>setShowPaywall(false)} title="Objekt-Limit erreicht" message={`Mit deinem aktuellen Plan kannst du maximal ${limit===Infinity?'unbegrenzt':limit} Objekte anlegen.`} />
      )}

      {/* Header */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24,gap:12,flexWrap:'wrap' }}>
        <div>
          <h1 style={{ margin:'0 0 2px',fontSize:isMobile?22:28,fontWeight:800,letterSpacing:'-0.6px' }}>Objekte</h1>
          {!loading && <div style={{ fontSize:13,color:T.muted }}>{properties.length} {properties.length === 1 ? 'Objekt' : 'Objekte'} verwaltet</div>}
        </div>
        <div style={{ display:'flex',gap:10 }}>
          <button onClick={()=>setShowBerichte(true)} style={{ display:'flex',alignItems:'center',gap:6,padding:'10px 16px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',color:T.ink,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Berichte
          </button>
          <button onClick={startCreate} style={{ display:'flex',alignItems:'center',gap:6,padding:'10px 18px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Objekt hinzufügen
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display:'flex',gap:8,marginBottom:20,alignItems:'center',flexWrap:'wrap' }}>
        <div style={{ position:'relative',width:220 }}>
          <svg style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Objekte durchsuchen…"
            style={{ width:'100%',boxSizing:'border-box',border:`1.5px solid ${T.border}`,borderRadius:9,padding:'8px 12px 8px 30px',fontSize:13,fontFamily:T.font,color:T.ink,outline:'none' }}
            onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
        </div>

        {/* Kategorie chip */}
        <div style={{ display:'flex',alignItems:'center',gap:4,padding:'7px 12px',borderRadius:99,border:`1.5px solid ${filterKategorie?T.ink:T.border}`,background:filterKategorie?'#f5f5f5':'white',cursor:'pointer',fontSize:12,fontWeight:600,color:filterKategorie?T.ink:T.muted }}
          onClick={()=>setFilterK(null)}>
          Kategorie {filterKategorie&&`· ${PROP_TYPE_LABEL[filterKategorie]}`}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          {filterKategorie && <span onClick={e=>{e.stopPropagation();setFilterK(null);}} style={{ marginLeft:2 }}>×</span>}
        </div>

        <div style={{ display:'flex',alignItems:'center',gap:4,padding:'7px 12px',borderRadius:99,border:`1.5px solid ${filterZahlung?T.ink:T.border}`,background:filterZahlung?'#f5f5f5':'white',cursor:'pointer',fontSize:12,fontWeight:600,color:filterZahlung?T.ink:T.muted }}
          onClick={()=>setFilterZ(null)}>
          Zahlungsstatus
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          {filterZahlung && <span onClick={e=>{e.stopPropagation();setFilterZ(null);}} style={{ marginLeft:2 }}>×</span>}
        </div>

        {(search||filterKategorie||filterZahlung) && (
          <button onClick={()=>{setSearch('');setFilterK(null);setFilterZ(null);}} style={{ background:'none',border:'none',cursor:'pointer',fontSize:12,color:T.ink,fontWeight:600,fontFamily:T.font }}>
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:16 }}>
          {[0,1,2].map(i=>(
            <div key={i} style={{ background:'white',border:`1px solid ${T.border}`,borderRadius:14,padding:22 }}>
              <Sk w="55%" h={18} r={6} /><div style={{ height:8 }}/><Sk w="80%" h={13} r={5} />
            </div>
          ))}
        </div>
      ) : filtered.length===0 ? (
        <div style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:14,overflow:'hidden' }}>
          {search||filterKategorie ? (
            <div style={{ textAlign:'center', padding:'60px 24px' }}>
              <div style={{ width:56,height:56,background:T.bg,borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
              <div style={{ fontSize:16,fontWeight:700,color:T.ink,marginBottom:8 }}>Keine Objekte gefunden</div>
              <div style={{ fontSize:13,color:T.muted,lineHeight:1.7,marginBottom:20 }}>Versuche andere Filter oder setze die Suche zurück.</div>
              <button onClick={()=>{setSearch('');setFilterK(null);}} className="btn-secondary">Filter zurücksetzen</button>
            </div>
          ) : (
            <div style={{ padding:'48px 40px' }}>
              <div style={{ display:'flex',alignItems:'flex-start',gap:32,marginBottom:40 }}>
                <div style={{ flexShrink:0,width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',border:`1.5px solid ${T.border}` }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:20,fontWeight:700,color:T.ink,marginBottom:8,letterSpacing:'-0.3px' }}>Noch keine Objekte vorhanden</div>
                  <div style={{ fontSize:14,color:T.muted,lineHeight:1.8,maxWidth:520 }}>Verwalte all deine Immobilien an einem Ort — von der Wohnung bis zur Gewerbeimmobilie. Erfasse Mieter, Zahlungen und Dokumente pro Objekt.</div>
                  <button onClick={startCreate} className="btn-primary" style={{ marginTop:20 }}>+ Erstes Objekt anlegen</button>
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)',gap:16 }}>
                {[
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>, title:'Mieter zuordnen', text:'Weise jedem Objekt direkt deine Mieter zu und verwalte Mietverhältnisse.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>, title:'Zahlungen verfolgen', text:'Behalte Mietzahlungen und Ausstände pro Objekt im Blick.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, title:'Dokumente ablegen', text:'Speichere Mietverträge, Übergabeprotokolle und mehr pro Objekt.' },
                ].map((f,i)=>(
                  <div key={i} style={{ background:T.bg,borderRadius:12,padding:'18px 20px',border:`1px solid ${T.border}` }}>
                    <div style={{ width:36,height:36,background:'white',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12,border:`1px solid ${T.border}`,boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                    <div style={{ fontSize:13,fontWeight:700,color:T.ink,marginBottom:4 }}>{f.title}</div>
                    <div style={{ fontSize:12,color:T.muted,lineHeight:1.6 }}>{f.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:14 }}>
          {filtered.map(p=>{
            const assigned = tenants.filter(t=>t.property_id===p.id);
            const totalRent = assigned.reduce((s,t)=>s+(Number(t.monthly_rent)||0),0);
            return (
              <div key={p.id}
                onClick={()=>{setDetailProp(p);setView('detail');}}
                style={{ background:'white',border:`1.5px solid ${T.border}`,borderRadius:12,overflow:'hidden',cursor:'pointer',transition:'border-color 0.15s',display:'flex' }}
                onMouseEnter={e=>e.currentTarget.style.borderColor=T.ink}
                onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}
              >
                {/* Left accent */}
                <div style={{ width:4,flexShrink:0,background:assigned.length>0?T.ink:T.border }} />
                <div style={{ padding:'18px 20px',flex:1 }}>
                  <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:6 }}>
                    <h3 style={{ margin:0,fontSize:15,fontWeight:700,color:T.ink,lineHeight:1.3 }}>{p.name}</h3>
                    <span style={{ flexShrink:0,fontSize:10,fontWeight:700,background:T.bg,color:T.muted,padding:'3px 9px',borderRadius:99,textTransform:'uppercase',letterSpacing:'0.5px' }}>
                      {PROP_TYPE_LABEL[p.property_type||p.type]||'Objekt'}
                    </span>
                  </div>
                  {fullAddr(p) && <div style={{ fontSize:12,color:T.muted,marginBottom:12 }}>{fullAddr(p)}</div>}
                  <div style={{ display:'flex',gap:16,flexWrap:'wrap' }}>
                    <span style={{ fontSize:12,color:assigned.length>0?T.ink:T.muted,fontWeight:assigned.length>0?600:400 }}>
                      {assigned.length} {assigned.length===1?'Mieter':'Mieter'}
                    </span>
                    {totalRent > 0 && <span style={{ fontSize:12,color:T.muted }}>{fmtEur(totalRent)} / Mo.</span>}
                    {p.area && <span style={{ fontSize:12,color:T.muted }}>{p.area} m²</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showBerichte && <BerichteModal properties={properties} tenants={tenants} onClose={()=>setShowBerichte(false)} />}
    </div>
  );
}
